import moment from "moment";
import React, { useContext } from "react";
import { Button, Dropdown, Table, Badge } from "react-bootstrap";
import checkPermissions from "@beeldit/user-and-access/auth/services/checkPermissions";
import checkRoles from "@beeldit/user-and-access/auth/services/checkRoles";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { FinalTableConfig } from "../models/FinalTableConfig.interface";
import { User } from "@beeldit/user-and-access/users/models/User";

import { useTranslation } from "react-i18next";
import { t } from "i18next";

import "../scss/BeelditTable.css";
import {
  CaretDown,
  CaretUp,
  CaretUpDown,
  Check,
  X,
} from "@phosphor-icons/react";

interface Props {
  config: FinalTableConfig;
  data: {
    elements: any[];
    totalElements: number;
  };
  orderBy?: any;
  onOrderByChange?: any;
}

function BeelditTable(props: Props) {
  const { columns, rowActions, rowConfiguration } = props.config;
  const { elements } = props.data;
  const { orderBy, onOrderByChange } = props;

  const { user } = useContext(AuthContext);

  const { t } = useTranslation();

  // Filter columns by show, permissions, and roles
  const visibleColumns = columns.filter(
    (col) =>
      col.show !== false &&
      (!col.permissions || checkPermissions(user, col.permissions)) &&
      (!col.roles || checkRoles(user, col.roles))
  );

  return (
    <>
      {/* <pre>{JSON.stringify(elements, null, 2)}</pre>
      <pre>{JSON.stringify(columns, null, 2)}</pre> */}

      <Table hover responsive size="sm">
        <thead>
          <tr>
            {user ? (
              <>
                {visibleColumns.map((column) => {
                  let className = "text-uppercase " + column.type;
                  return (
                    <th className={className} key={column.key}>
                      {column.sortable ? (
                        <>
                          {orderBy === "-" + column.name ? (
                            <CaretDown
                              size={22}
                              className="click"
                              onClick={() => {
                                onOrderBy(
                                  column.name,
                                  orderBy,
                                  onOrderByChange
                                );
                              }}
                            />
                          ) : null}
                          {orderBy === "+" + column.name ? (
                            <CaretUp
                              size={22}
                              className="click"
                              onClick={() => {
                                onOrderBy(
                                  column.name,
                                  orderBy,
                                  onOrderByChange
                                );
                              }}
                            />
                          ) : null}
                          {orderBy !== "+" + column.name &&
                          orderBy !== "-" + column.name ? (
                            <CaretUpDown
                              size={16}
                              className="click"
                              onClick={() => {
                                onOrderBy(
                                  column.name,
                                  orderBy,
                                  onOrderByChange
                                );
                              }}
                            />
                          ) : null}
                        </>
                      ) : null}
                      {t(column.label)}
                    </th>
                  );
                })}
                {rowActions && rowActions.length > 0 && (
                  <th className="action-column text-uppercase">
                    {t("Actions")}
                  </th>
                )}
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {renderElements(
            elements,
            visibleColumns, // pass only visible columns
            rowActions,
            rowConfiguration,
            user
          )}
        </tbody>
      </Table>
    </>
  );
}

function onOrderBy(label: string, orderBy: string, onOrderByChange: any) {
  let newOrderBy;
  if (orderBy) {
    const direction = orderBy.substring(0, 1);
    const labelToOrder = orderBy.substring(1);
    if (label === labelToOrder) {
      newOrderBy = direction === "+" ? "-" + label : "+" + label;
    } else {
      newOrderBy = "+" + label;
    }
  } else {
    newOrderBy = "+" + label;
  }
  onOrderByChange(newOrderBy);
}

function renderElements(
  elements: any[],
  columns: any[],
  rowActions: any[],
  rowConfiguration: any,
  user: User | null
): any {
  return elements.map((element) => (
    <tr className={rowConfiguration.class(element)} key={element.id}>
      {renderElement(element, columns, rowActions, user)}
      {rowActions &&
        rowActions.length > 0 &&
        renderRowActions(rowActions, element, user)}
    </tr>
  ));
}

function renderElement(
  element: any,
  columns: any[],
  rowActions: any[],
  user: User | null
): any {
  return columns.map((column) => {
    let className = column.type;
    // Add wrap-text class for product name column
    if (column.key === "name.es") {
      className += " wrap-text";
    }
    return (
      <td key={column.key} className={className}>
        {getFieldValue(element, column.key, column)}
      </td>
    );
  });
}

function renderRowActions(
  rowActions: any[],
  element: any,
  user: User | null
): any {
  return (
    <td key="action" className="actions-buttons text-right">
      {rowActions.map((rowAction) => {
        if (
          checkPermissions(user, rowAction.permissions) &&
          checkRoles(user, rowAction.roles) &&
          checkShow(rowAction.show, element, user)
        ) {
          return (
            // Si rowAction no tiene actions se muestra el botón
            !rowAction.actions ? (
              <Button
                className="icon-button mx-1 rounded-circle button-action btn-action btn btn-primary"
                key={rowAction.label}
                onClick={() =>
                  rowAction.action(getActionData(rowAction, element))
                }
              >
                {rowAction.icon}
              </Button>
            ) : (
              // Si rowActions tiene actions mostramos el botón con el icon y el dropdown. En el dropdown se muestran los actions
              <Dropdown key={rowAction.label}>
                <Dropdown.Toggle
                  variant="link"
                  id="dropdown-basic"
                  className="icon-button"
                >
                  {rowAction.icon}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {rowAction.actions.map((action: any) => {
                    if (
                      checkPermissions(user, action.permissions) &&
                      checkRoles(user, action.roles) &&
                      checkShow(action.show, element, user)
                    ) {
                      return (
                        <Dropdown.Item
                          key={action.label}
                          onClick={() =>
                            action.action(getActionData(action, element))
                          }
                        >
                          {action.icon}
                          <span className="ms-2">{t(action.label)}</span>
                        </Dropdown.Item>
                      );
                    }
                    return null;
                  })}
                </Dropdown.Menu>
              </Dropdown>
            )
          );
        }
        return null;
      })}
    </td>
  );
}

function getFieldValue(element: any, key: string, column: any): any {
  const keys = key.split(".");

  for (let key of keys) {
    if (element[key] === undefined || element[key] == null) {
      return null;
    }
    element = element[key];
  }
  if (column.type === "boolean") {
    return element ? <Check size={22} /> : <X size={22} />;
  }

  if (column.type === "url") {
    if (element.includes("http")) {
      return (
        <a target="_blank" rel="noreferrer" href={element}>
          {element}
        </a>
      );
    }
  }

  if (column.type === "currency" && column.currency) {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: column.currency,
    }).format(element);
  }
  if (column.type === "array") {
    return element.map((el: any, idx: number) => {
      return (
        <React.Fragment key={idx}>
          {el[column.attribute]}
          <br />
        </React.Fragment>
      );
    });
  }
  if (column.type === "concat") {
    return column.fields.map((field: any, index: number, array: any[]) => {
      if (element[field]) {
        return (
          <React.Fragment key={field ?? index}>
            {element[field]}
            {index < array.length - 1 && " "}
          </React.Fragment>
        );
      }
      return null;
    });
  }
  if (column.type === "array-first-element") {
    if (element.length) {
      return column.attribute.split(".").reduce((obj: any, key: any) => {
        return (
          obj && element[0][obj] && element[0][obj][key], element[0][obj][key]
        );
      });
    }
    return "";
  }
  if (column.badge && column.badge_type) {
    const value = format(element, column);
    let type = "light";
    if (typeof column.badge_type === "string") {
      type = column.badge_type;
    } else if (typeof column.badge_type === "function") {
      type = column.badge_type(element);
    }
    return <Badge bg={type}>{value}</Badge>;
  }

  element = format(element, column);
  return element;
}

function format(value: any, column: any): any {
  if (column.type === "date") {
    value = moment(value, "YYYY-MM-DD");
    return value.format("DD/MM/YYYY");
  } else if (column.type === "datetime") {
    value = moment(value);
    return value.format("DD/MM/YYYY HH:mm");
  } else if (column.type === "translation") {
    const prefix = column.translation_prefix;
    value = t(prefix + "_" + value);
    return value;
  } else if (column.type === "percentage") {
    value = value + "%";
    return value;
  }
  return value;
}

function getActionData(action: any, element: any): any {
  let data = action.data === undefined ? "id" : action.data;

  if (data) {
    data = data.split(".");

    for (let key of data) {
      if (element[key] === undefined || !element[key]) {
        return null;
      }

      element = element[key];
    }
  }

  return element;
}

function checkShow(show: any, element: any, user: any): boolean {
  if (typeof show === "boolean") {
    return show;
  } else if (typeof show === "function") {
    const res = show(element, user);
    return res;
  }
  return false;
}

export default BeelditTable;
